import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShoppingBag, Minus, Plus, ArrowLeft } from 'lucide-react';
import { menuAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from './templates/shared';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import StockBadge from '../../components/StockBadge'
import { t } from '../../i18n'

// The ecommerce storefront's dedicated product page - its own URL, full
// image gallery, and proper variant selection, instead of a popup modal
// bolted onto the catalog grid.
export default function ProductDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tenant = useTenant();
  const branch = tenant.id || searchParams.get('branch') || '1';
  const query = location.search;

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  // Chosen options: at most one, or several when the product allows it.
  const [selectedVariants, setSelectedVariants] = useState([]);
  const toggleVariant = variant => setSelectedVariants(prev => {
    const picked = prev.some(v => v.id === variant.id);
    if (item?.variant_selection === 'multiple') return picked ? prev.filter(v => v.id !== variant.id) : [...prev, variant];
    return picked ? [] : [variant];
  });
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');

  const { addItem, openDrawer } = useCart();
  const currency = useCurrency();
  const customTheme = tenant.settings?.custom_theme?.active ? tenant.settings.custom_theme : null;
  // Layout always comes from the Store Templates setting, never from
  // custom_theme - see the note in EcommerceStorefront.jsx.
  const style = tenant.settings?.store_template || 'default';

  useEffect(() => {
    setLoading(true);
    menuAPI.getPublicItem(id, { branchId: parseInt(branch) })
      .then(({ data }) => { setItem(data.item); setActiveImage(0); setSelectedVariants([]); setSelectedModifiers([]); setQuantity(1); })
      .catch(() => toast.error(t("This product could not be found")))
      .finally(() => setLoading(false));
  }, [id, branch]);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-gray-400">{t("Loading product...")}</div>;
  }
  if (!item) {
    return (
      <div className="text-center py-24">
        <p className="sk-muted mb-4">{t("This product could not be found.")}</p>
        <Link to={`/${query}`} className="sk-btn">{t("Back to the collection")}</Link>
      </div>
    );
  }

  const images = item.images?.length > 0 ? item.images : ['https://images.unsplash.com/photo-1560769629-975ec94e6a86?w=800&h=800&fit=crop'];
  const basePrice = parseFloat(item.price || 0);
  const variantPrice = selectedVariants.reduce((sum, v) => sum + parseFloat(v.price_adjustment || 0), 0);
  const modifierTotal = selectedModifiers.reduce((sum, m) => sum + parseFloat(m.extra_price || 0), 0);
  const unitPrice = basePrice + variantPrice + modifierTotal;

  const toggleModifier = (modifier) => {
    setSelectedModifiers(prev =>
      prev.find(m => m.id === modifier.id) ? prev.filter(m => m.id !== modifier.id) : [...prev, modifier]
    );
  };

  const handleAddToCart = () => {
    addItem(item, quantity, selectedModifiers, note, parseInt(branch), null, selectedVariants);
    toast.success(t("{name} added to cart", { name: item.name }));
    openDrawer();
  };

  return (
    <div className={`product-detail catalog-${style}`}>
      <Link to={`/${query}`} className="product-detail-back"><ArrowLeft size={16} /> {t("Back to collection")}</Link>

      <div className="product-detail-grid">
        <div className="product-gallery">
          <div className="product-gallery-main">
            <img src={images[activeImage]} alt={item.name} />
            {images.length > 1 && (
              <>
                <button aria-label={t("Previous image")} className="product-gallery-nav left" onClick={() => setActiveImage(i => (i - 1 + images.length) % images.length)}>
                  <ChevronLeft size={20} />
                </button>
                <button aria-label={t("Next image")} className="product-gallery-nav right" onClick={() => setActiveImage(i => (i + 1) % images.length)}>
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="product-gallery-thumbs">
              {images.map((img, i) => (
                <button key={i} className={i === activeImage ? 'active' : ''} onClick={() => setActiveImage(i)} aria-label={t("View image {v0}", { v0: i + 1 })}>
                  <img src={img} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="product-info">
          {item.category_name && <p className="eyebrow">{item.category_name}</p>}
          <h1>{item.name}</h1>
          <p className="product-price">{unitPrice.toFixed(2)} <small>{currency}</small></p>
          {item.description && <p className="product-description">{t(item.description)}</p>}

          {item.variants?.length > 0 && (
            <div className="product-options">
              <h3>{t("Options")}{item.variant_selection === 'multiple' && <small> · {t("choose any")}</small>}</h3>
              <div className="product-variant-list">
                {item.variants.map(variant => (
                  <button
                    key={variant.id}
                    className={selectedVariants.some(v => v.id === variant.id) ? 'active' : ''}
                    aria-pressed={selectedVariants.some(v => v.id === variant.id)}
                    onClick={() => toggleVariant(variant)}
                  >
                    {variant.name}
                    {parseFloat(variant.price_adjustment || 0) !== 0 && (
                      <span>{variant.price_adjustment > 0 ? '+' : ''}{parseFloat(variant.price_adjustment).toFixed(2)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.modifiers?.length > 0 && (
            <div className="product-options">
              <h3>{t("Add-ons")}</h3>
              <div className="product-modifier-list">
                {item.modifiers.map(modifier => (
                  <label key={modifier.id}>
                    <input type="checkbox" checked={selectedModifiers.some(m => m.id === modifier.id)} onChange={() => toggleModifier(modifier)} />
                    <span>{modifier.name}</span>
                    {parseFloat(modifier.extra_price || 0) > 0 && <em>+{parseFloat(modifier.extra_price).toFixed(2)}</em>}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="product-options">
            <h3>{t("Notes (optional)")}</h3>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={t("Anything we should know?")} />
          </div>

          {(item.in_stock === false || item.stock_left) && <StockBadge item={item} className="mb-3" />}
          <div className="product-actions">
            <div className="product-qty">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1} aria-label={t("Decrease quantity")}><Minus size={15} /></button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)} disabled={item.stock_left != null && quantity >= item.stock_left} aria-label={t("Increase quantity")}><Plus size={15} /></button>
            </div>
            {item.in_stock === false ? (
              <button className="product-add-to-cart" disabled>{t("Out of stock")}</button>
            ) : (
              <button className="product-add-to-cart" onClick={handleAddToCart} disabled={item.is_available === false || item.is_available === 0}>
                <ShoppingBag size={17} /> {t("Add to cart ·")} {(unitPrice * quantity).toFixed(2)} {currency}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
